import { List, useTable } from "@refinedev/antd";
import { ShowButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import {
  Button,
  Form,
  Input,
  Popover,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { useState } from "react";
import { IconDisplay } from "../../components/IconDisplay";
import { formatSchedule } from "./schedule";

const { Title } = Typography;

export function TournamentsList() {
  const [hoveredRow, setHoveredRow] = useState<string>();

  // Fetch categories for the filter dropdown
  const { data: categoriesData } = useList({
    resource: "category",
    filters: [{ field: "status", operator: "eq", value: "ACTIVE" }],
    pagination: { pageSize: 100 },
  });
  const categories = categoriesData?.data || [];

  const { tableProps, searchFormProps } = useTable({
    resource: "tournament",
    syncWithLocation: true,
    queryOptions: {
      keepPreviousData: false,
      cacheTime: 0,
      staleTime: 0,
    },
    meta: {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    },
    onSearch: (values: any) => {
      return [
        {
          field: "q",
          operator: "contains",
          value: values.q,
        },
        {
          field: "status",
          operator: "eq",
          value: values.status,
        },
        {
          field: "categoryID",
          operator: "eq",
          value: values.categoryID,
        },
      ];
    },
  });

  const handleSearchChange = debounce(() => {
    searchFormProps.form?.submit();
  }, 500);

  const columns = [
    {
      title: "Media",
      dataIndex: "thumbnailURL",
      render: (thumbnailURL: string) => (
        <IconDisplay iconURL={thumbnailURL} width={36} height={36} borderRadius="8px" />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Creator",
      dataIndex: "creatorName",
    },
    {
      title: "Collaborators",
      dataIndex: "collaborators",
      render: (value: string[] | null | undefined) => (value && value.length > 0 ? value.join(", ") : "-"),
    },
    {
      title: "Mode",
      dataIndex: "tournamentMode",
    },
    {
      title: "Start",
      dataIndex: "startDateTime",
      render: (value: string) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Schedule",
      dataIndex: "recurring",
      render: (_: any, record: any) => {
        const value = formatSchedule(record?.recurring);
        return (
          <Tooltip title={value || "-"} placement="top">
            <span>{value || "-"}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Product",
      dataIndex: "productName",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Categories",
      dataIndex: "categories",
      render: (value: { id: string; title: string }[]) => {
        if (!value || value.length === 0) return "-";
        return (
          <Space wrap size={[0, 4]}>
            {value.map((cat) => (
              <Tag key={cat.id} color="cyan">{cat.title}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (value: string) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "Actions",
      render: (_: any, record: any) => (
        <Space>
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "4px", position: "relative" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          width: "100%",
          justifyContent: "flex-end",
          alignItems: "flex-start",
        }}
      >
        <Form {...searchFormProps} onValuesChange={handleSearchChange}>
          <Title level={4}>Filter and Search</Title>
          <div style={{ display: "flex", gap: 10 }}>
            <Popover
              placement="bottomRight"
              trigger="click"
              content={
                <>
                  <Form.Item name="status" label="Status">
                    <Select placeholder="Select status" allowClear style={{ width: 220 }}>
                      <Select.Option value="PUBLISHED">PUBLISHED</Select.Option>
                      <Select.Option value="DRAFT">DRAFT</Select.Option>
                      <Select.Option value="DELETED">DELETED</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="categoryID" label="Category">
                    <Select
                      placeholder="Select category"
                      allowClear
                      style={{ width: 220 }}
                      showSearch
                      optionFilterProp="children"
                    >
                      {categories.map((cat: any) => (
                        <Select.Option key={cat.id} value={cat.id}>{cat.title}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              }
            >
              <Button icon={<FilterOutlined />}>Filters</Button>
            </Popover>
            <Form.Item name="q">
              <Input placeholder="Search by name, creator, product, type..." />
            </Form.Item>
          </div>
        </Form>
      </div>

      <List>
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          onRow={(record: any) => ({
            onMouseEnter: () => setHoveredRow(record.id),
            onMouseLeave: () => setHoveredRow(""),
          })}
        />
      </List>
    </div>
  );
}


